import type { LayerSpecification, StyleSpecification } from 'maplibre-gl';
import type { Basemap } from 'src/shared/components/map/useMaplibreBasemap';
import type { MapCamera } from 'src/shared/components/map/MapCanvas';
import type { StacItem } from 'src/shared/state/api-slices/stacCatalogueEngineSlice';
import { tileSourceOf, tileUrlTemplate } from 'src/shared/catalogue/tileSource';

const FOOTPRINT = 'rgb(7, 103, 105)';
const PADDING = 48;

type Position = [number, number];

/** Every coordinate pair in a GeoJSON geometry, at whatever depth it nests. */
const positionsIn = (coordinates: unknown): Position[] => {
    if (!Array.isArray(coordinates)) {
        return [];
    }
    if (typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') {
        return [[coordinates[0], coordinates[1]]];
    }
    return coordinates.flatMap(positionsIn);
};

const positionsOf = (item: StacItem): Position[] => {
    const stated = item.bbox;
    if (stated?.length === 4) {
        return [
            [stated[0], stated[1]],
            [stated[2], stated[3]],
        ];
    }
    const geometry = item.geometry as { coordinates?: unknown } | null | undefined;
    return geometry ? positionsIn(geometry.coordinates) : [];
};

/**
 * Where the map opens on everything in force.
 *
 * A set rather than one version: a mosaic resolves to several tiles at one instant, so the map
 * has to hold all of them (ADR-016 decision 10).
 */
export const cameraForItems = (items: StacItem[]): MapCamera | null => {
    const positions = items.flatMap(positionsOf);
    if (positions.length === 0) {
        return null;
    }

    const longitudes = positions.map(([longitude]) => longitude);
    const latitudes = positions.map(([, latitude]) => latitude);

    return {
        bounds: [
            [Math.min(...longitudes), Math.min(...latitudes)],
            [Math.max(...longitudes), Math.max(...latitudes)],
        ],
        padding: PADDING,
    };
};

/** The kinds of mark an archive is drawn with, and so the layers a pointer can land on. */
const TILE_LAYER_KINDS = ['fill', 'line', 'point'] as const;

/**
 * The layers drawn from each version's archive.
 *
 * Shared with the map so hover listens to exactly what carries a feature's fields — listening
 * to the whole map instead would catch the basemap and the footprint outline, neither of which
 * has any.
 */
export const tileLayerIdsFor = (items: StacItem[], servedLayers: Record<string, string[]>): string[] =>
    items
        .filter(item => tileSourceOf(item, servedLayers[item.id]) !== null)
        .flatMap(item => TILE_LAYER_KINDS.map(kind => `tiles-${item.id}-${kind}`));

export type CatalogueMapStyleArgs = {
    /** Everything in force at the instant the timeline is pointed at. */
    items: StacItem[];
    collectionId: string;
    engineApiUrl: string;
    basemap: Basemap;
    /** What the engine read out of each version's archive, keyed by version id. */
    servedLayers?: Record<string, string[]>;
};

/** The footprints as one collection: an outline of *where* each version applies. */
const footprintFeatures = (items: StacItem[]) => ({
    type: 'FeatureCollection' as const,
    features: items
        .filter(item => item.geometry)
        .map(item => ({ type: 'Feature' as const, geometry: item.geometry, properties: { id: item.id } })),
});

/**
 * What the catalogue draws at one instant: the footprints of every version in force, and the
 * tiles of any that carries an archive.
 *
 * Fill, line and point for each archive, because nothing tells us which the layer holds — the
 * media type distinguishes an archive from a data file, not vectors from images, and no reader
 * for the archive header exists yet (ADR-021). Drawing all three costs nothing for the kinds
 * that are absent.
 */
export const buildCatalogueMapStyle = ({
    items,
    collectionId,
    engineApiUrl,
    basemap,
    servedLayers,
}: CatalogueMapStyleArgs): StyleSpecification => {
    const tiled = items
        .map(item => ({ item, source: tileSourceOf(item, servedLayers?.[item.id]) }))
        .filter((held): held is { item: StacItem; source: NonNullable<ReturnType<typeof tileSourceOf>> } => held.source !== null);

    const tileSources = Object.fromEntries(
        tiled.map(({ item, source }) => [
            `tiles-${item.id}`,
            {
                type: 'vector' as const,
                tiles: [tileUrlTemplate(engineApiUrl, collectionId, item.id)],
                // The tile grid every archive is built on, which cataloguing enforces.
                minzoom: 0,
                maxzoom: 14,
                attribution: source.layer,
            },
        ])
    );

    const tileLayers: LayerSpecification[] = tiled.flatMap(({ item, source }) => {
        const from = { source: `tiles-${item.id}`, 'source-layer': source.layer };
        return [
            {
                ...from,
                id: `tiles-${item.id}-fill`,
                type: 'fill' as const,
                filter: ['==', ['geometry-type'], 'Polygon'],
                paint: { 'fill-color': FOOTPRINT, 'fill-opacity': 0.35 },
            },
            {
                ...from,
                id: `tiles-${item.id}-line`,
                type: 'line' as const,
                filter: ['==', ['geometry-type'], 'LineString'],
                paint: { 'line-color': FOOTPRINT, 'line-width': 1.5 },
            },
            {
                ...from,
                id: `tiles-${item.id}-point`,
                type: 'circle' as const,
                filter: ['==', ['geometry-type'], 'Point'],
                paint: { 'circle-radius': 3, 'circle-color': FOOTPRINT },
            },
        ];
    });

    return {
        version: 8,
        // CAUTION: name each only when the basemap carries it. A key set to `undefined` fails
        // style validation, and MapLibre then draws nothing at all.
        ...(basemap.style?.glyphs ? { glyphs: basemap.style.glyphs } : {}),
        ...(basemap.style?.sprite ? { sprite: basemap.style.sprite } : {}),
        sources: {
            ...(basemap.style?.sources ?? {}),
            footprints: { type: 'geojson', data: footprintFeatures(items) as GeoJSON.FeatureCollection },
            ...tileSources,
        },
        layers: [
            ...(basemap.style?.layers ?? []),
            // The footprint is only an outline of where a version applies, so it sits under the
            // data itself rather than over it.
            {
                id: 'footprints-outline',
                type: 'line',
                source: 'footprints',
                paint: { 'line-color': FOOTPRINT, 'line-width': 1, 'line-dasharray': [2, 2] },
            },
            ...tileLayers,
        ],
    };
};
